/**
 * 阶段产物展示组件
 * 根据不同阶段展示结构化的产物数据
 */

import React from 'react'
import {
  Card,
  Descriptions,
  Tag,
  Space,
  List,
  Row,
  Col,
  Typography,
  Timeline,
  Progress,
  Divider,
  Alert,
} from 'antd'
import {
  FileTextOutlined,
  TeamOutlined,
  GlobalOutlined,
  CrownOutlined,
  ExperimentOutlined,
  GiftOutlined,
  PictureOutlined,
  CheckCircleOutlined,
} from '@ant-design/icons'
import type {
  PhaseResult,
  Phase1Result,
  Phase2Result,
  Phase3Result,
  Phase4Result,
  Phase5Result,
  Phase6Result,
  Phase7Result,
} from '@/types/scriptGeneration'

const { Text, Title, Paragraph } = Typography

// 代码样式
const codeStyle: React.CSSProperties = {
  margin: 0,
  padding: 12,
  background: '#1e1e1e',
  borderRadius: 4,
  fontSize: 12,
  fontFamily: '"Fira Code", "Monaco", "Consolas", monospace',
  color: '#d4d4d4',
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-all',
  maxHeight: 300,
  overflow: 'auto',
}

// ============================================================================
// 阶段图标映射
// ============================================================================

const PHASE_ICONS: Record<number, React.ReactNode> = {
  1: <FileTextOutlined />,
  2: <CrownOutlined />,
  3: <GlobalOutlined />,
  4: <TeamOutlined />,
  5: <ExperimentOutlined />,
  6: <GiftOutlined />,
  7: <PictureOutlined />,
  8: <CheckCircleOutlined />,
}

// ============================================================================
// Phase 1: 文本预处理产物
// ============================================================================

interface Phase1ViewerProps {
  data: Phase1Result | null | undefined
}

const Phase1Viewer: React.FC<Phase1ViewerProps> = ({ data }) => {
  // Defensive null checks
  if (!data) {
    return <Alert message="暂无数据" type="warning" showIcon />
  }

  // v5 T1 格式: era, world_setting, spaces, world_npcs
  const era = (data as Record<string, unknown>).era as string | undefined
  const world_setting = (data as Record<string, unknown>).world_setting as string | undefined
  const spaces = (data as Record<string, unknown>).spaces as Array<{ id: string; name: string; type: string; description: string }> | undefined
  const world_npcs = (data as Record<string, unknown>).world_npcs as Array<{ id: string; name: string; role: string; description: string }> | undefined

  // 如果是 v5 T1 格式
  if (era) {
    return (
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        <Card size="small" title="世界圣经">
          <Descriptions column={1} size="small">
            <Descriptions.Item label="时代">{era}</Descriptions.Item>
            <Descriptions.Item label="世界设定">{world_setting || '-'}</Descriptions.Item>
          </Descriptions>
        </Card>

        {spaces && spaces.length > 0 && (
          <Card size="small" title={`空间列表 (${spaces.length})`}>
            <List
              size="small"
              dataSource={spaces}
              renderItem={(space) => (
                <List.Item>
                  <Descriptions column={1} size="small" style={{ width: '100%' }}>
                    <Descriptions.Item label="名称">
                      <Tag color={space.type === 'public' ? 'blue' : 'orange'}>
                        {space.type === 'public' ? '公共' : '私密'}
                      </Tag>
                      {space.name}
                    </Descriptions.Item>
                    <Descriptions.Item label="描述">{space.description}</Descriptions.Item>
                  </Descriptions>
                </List.Item>
              )}
            />
          </Card>
        )}

        {world_npcs && world_npcs.length > 0 && (
          <Card size="small" title={`世界NPC (${world_npcs.length})`}>
            <List
              size="small"
              dataSource={world_npcs}
              renderItem={(npc) => (
                <List.Item>
                  <Descriptions column={1} size="small" style={{ width: '100%' }}>
                    <Descriptions.Item label="名称">{npc.name}</Descriptions.Item>
                    <Descriptions.Item label="角色">{npc.role}</Descriptions.Item>
                    <Descriptions.Item label="描述">{npc.description}</Descriptions.Item>
                  </Descriptions>
                </List.Item>
              )}
            />
          </Card>
        )}
      </Space>
    )
  }

  // v4 格式
  const structured_text = data.structured_text || {}
  const word_count = data.word_count || 0
  const characters = structured_text.characters || []
  const scenes = structured_text.scenes || []
  const dialogues = structured_text.dialogues || []

  return (
    <Space direction="vertical" style={{ width: '100%' }} size="middle">
      {/* 基本统计 */}
      <Card size="small" title="文本统计">
        <Row gutter={16}>
          <Col span={8}>
            <Descriptions column={1} size="small">
              <Descriptions.Item label="标题">{structured_text.title || '-'}</Descriptions.Item>
              <Descriptions.Item label="摘要">{structured_text.summary || '-'}</Descriptions.Item>
            </Descriptions>
          </Col>
          <Col span={8}>
            <Progress
              type="circle"
              percent={100}
              format={() => `${(word_count / 1000).toFixed(0)}k`}
              size={60}
            />
            <Text type="secondary" style={{ display: 'block', textAlign: 'center', marginTop: 8 }}>
              字数
            </Text>
          </Col>
          <Col span={8}>
            <Descriptions column={1} size="small">
              <Descriptions.Item label="角色数">{characters.length}</Descriptions.Item>
              <Descriptions.Item label="场景数">{scenes.length}</Descriptions.Item>
              <Descriptions.Item label="对话数">{dialogues.length}</Descriptions.Item>
            </Descriptions>
          </Col>
        </Row>
      </Card>

      {/* 角色列表 */}
      {characters.length > 0 && (
        <Card size="small" title="角色列表">
          <Space wrap>
            {characters.map((char, idx) => (
              <Tag key={idx} color="blue">{char}</Tag>
            ))}
          </Space>
        </Card>
      )}

      {/* 场景列表 */}
      {scenes.length > 0 && (
        <Card size="small" title="场景列表">
          <List
            size="small"
            dataSource={scenes}
            renderItem={(scene, idx) => (
              <List.Item>
                <Text>{idx + 1}. {scene}</Text>
              </List.Item>
            )}
          />
        </Card>
      )}

      {/* 对话列表 */}
      {dialogues.length > 0 && (
        <Card size="small" title={`对话列表 (${dialogues.length}条)`}>
          <List
            size="small"
            dataSource={dialogues}
            renderItem={(dialogue) => (
              <List.Item>
                <Descriptions column={1} size="small" style={{ width: '100%' }}>
                  <Descriptions.Item label="说话人">
                    <Tag color="green">{dialogue.speaker || '-'}</Tag>
                  </Descriptions.Item>
                  <Descriptions.Item label="内容">{dialogue.content || '-'}</Descriptions.Item>
                  {dialogue.emotion && (
                    <Descriptions.Item label="情绪">
                      <Tag>{dialogue.emotion}</Tag>
                    </Descriptions.Item>
                  )}
                </Descriptions>
              </List.Item>
            )}
          />
        </Card>
      )}
    </Space>
  )
}

// ============================================================================
// Phase 2: 剧本元信息产物
// ============================================================================

interface Phase2ViewerProps {
  data: Phase2Result | null | undefined
}

const Phase2Viewer: React.FC<Phase2ViewerProps> = ({ data }) => {
  // Defensive null checks
  if (!data) {
    return <Alert message="暂无数据" type="warning" showIcon />
  }

  // v5 T7 格式: phases, timeline
  const phases = (data as Record<string, unknown>).phases as Array<{
    id: string
    name: string
    type: string
    duration_minutes: number
    objective: string
  }> | undefined
  const timeline = (data as Record<string, unknown>).timeline as Array<{
    time: string
    event: string
  }> | undefined

  // 如果是 v5 T7 格式
  if (phases || timeline) {
    return (
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        {phases && phases.length > 0 && (
          <Card size="small" title={`游戏阶段 (${phases.length})`}>
            <List
              size="small"
              dataSource={phases}
              renderItem={(phase) => (
                <List.Item>
                  <Descriptions column={1} size="small" style={{ width: '100%' }}>
                    <Descriptions.Item label="阶段">
                      <Tag color="blue">{phase.type}</Tag>
                      {phase.name}
                    </Descriptions.Item>
                    <Descriptions.Item label="时长">{phase.duration_minutes}分钟</Descriptions.Item>
                    <Descriptions.Item label="目标">{phase.objective}</Descriptions.Item>
                  </Descriptions>
                </List.Item>
              )}
            />
          </Card>
        )}

        {timeline && timeline.length > 0 && (
          <Card size="small" title={`时间线 (${timeline.length})`}>
            <List
              size="small"
              dataSource={timeline}
              renderItem={(item) => (
                <List.Item>
                  <Space>
                    <Tag color="purple">{item.time}</Tag>
                    <Text>{item.event}</Text>
                  </Space>
                </List.Item>
              )}
            />
          </Card>
        )}
      </Space>
    )
  }

  // v4 格式
  const metadata = data.metadata || {}
  const theme = metadata.theme || []
  const key_plot_points = metadata.key_plot_points || []
  const required_elements = metadata.required_elements || []

  return (
    <Space direction="vertical" style={{ width: '100%' }} size="middle">
      <Card size="small" title="剧本元信息">
        <Descriptions column={2} size="small">
          <Descriptions.Item label="剧本ID">{data.script_id || '-'}</Descriptions.Item>
          <Descriptions.Item label="包ID">{data.package_id || '-'}</Descriptions.Item>
          <Descriptions.Item label="类型">
            <Tag color="purple">{metadata.genre || '-'}</Tag>
          </Descriptions.Item>
          <Descriptions.Item label="难度">
            <Tag color={metadata.difficulty === '困难' ? 'red' : 'blue'}>
              {metadata.difficulty || '-'}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label="预估时长">{metadata.estimated_duration || '-'}</Descriptions.Item>
          <Descriptions.Item label="目标受众">{metadata.target_audience || '-'}</Descriptions.Item>
          <Descriptions.Item label="核心冲突" span={2}>
            {metadata.core_conflict || '-'}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      {/* 主题标签 */}
      {theme.length > 0 && (
        <Card size="small" title="主题">
          <Space wrap>
            {theme.map((t, idx) => (
              <Tag key={idx} color="cyan">{t}</Tag>
            ))}
          </Space>
        </Card>
      )}

      {/* 关键情节点 */}
      {key_plot_points.length > 0 && (
        <Card size="small" title="关键情节点">
          <List
            size="small"
            dataSource={key_plot_points}
            renderItem={(point, idx) => (
              <List.Item>
                <Text>{idx + 1}. {point}</Text>
              </List.Item>
            )}
          />
        </Card>
      )}

      {/* 必需元素 */}
      {required_elements.length > 0 && (
        <Card size="small" title="必需元素">
          <Space wrap>
            {required_elements.map((el, idx) => (
              <Tag key={idx} color="gold">{el}</Tag>
            ))}
          </Space>
        </Card>
      )}
    </Space>
  )
}

// ============================================================================
// Phase 3: 世界观与派系产物
// ============================================================================

interface Phase3ViewerProps {
  data: Phase3Result | null | undefined
}

const Phase3Viewer: React.FC<Phase3ViewerProps> = ({ data }) => {
  // Defensive null checks
  if (!data) {
    return <Alert message="暂无数据" type="warning" showIcon />
  }

  // v5 T4 格式: events
  const events = (data as Record<string, unknown>).events as Array<{
    id: string
    name: string
    phase: string
    description: string
    purpose: string
  }> | undefined

  // 如果是 v5 T4 格式
  if (events && events.length > 0) {
    return (
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        <Card size="small" title={`公共事件 (${events.length})`}>
          <List
            size="small"
            dataSource={events}
            renderItem={(evt) => (
              <List.Item>
                <Descriptions column={1} size="small" style={{ width: '100%' }}>
                  <Descriptions.Item label="事件">
                    <Tag color="blue">{evt.phase}</Tag>
                    {evt.name}
                  </Descriptions.Item>
                  <Descriptions.Item label="描述">{evt.description}</Descriptions.Item>
                  <Descriptions.Item label="目的">{evt.purpose}</Descriptions.Item>
                </Descriptions>
              </List.Item>
            )}
          />
        </Card>
      </Space>
    )
  }

  // v4 格式
  const world = data.world || {}
  const factions = data.factions || []
  const core_rules = world.core_rules || []
  const restrictions = world.restrictions || []

  return (
    <Space direction="vertical" style={{ width: '100%' }} size="middle">
      {/* 世界观 */}
      <Card size="small" title="世界观">
        <Descriptions column={2} size="small">
          <Descriptions.Item label="名称">
            <Title level={5}>{world.name || '-'}</Title>
          </Descriptions.Item>
          <Descriptions.Item label="时代">{world.era || '-'}</Descriptions.Item>
          <Descriptions.Item label="地点">{world.location || '-'}</Descriptions.Item>
          <Descriptions.Item label="描述" span={2}>
            <Paragraph style={{ margin: 0 }}>{world.description || '-'}</Paragraph>
          </Descriptions.Item>
        </Descriptions>
      </Card>

      {/* 核心规则 */}
      {core_rules.length > 0 && (
        <Card size="small" title="核心规则">
          <List
            size="small"
            dataSource={core_rules}
            renderItem={(rule, idx) => (
              <List.Item>
                <Text>{idx + 1}. {rule}</Text>
              </List.Item>
            )}
          />
        </Card>
      )}

      {/* 限制 */}
      {restrictions.length > 0 && (
        <Card size="small" title="限制">
          <Space wrap>
            {restrictions.map((r, idx) => (
              <Tag key={idx} color="red">{r}</Tag>
            ))}
          </Space>
        </Card>
      )}

      <Divider />

      {/* 派系 */}
      <Title level={5}>派系 ({factions.length})</Title>
      {factions.length > 0 ? (
        <Row gutter={16}>
          {factions.map((faction, idx) => (
            <Col span={12} key={idx}>
              <Card size="small" title={faction.name || '-'} extra={<Tag>{faction.id || '-'}</Tag>}>
                <Descriptions column={1} size="small">
                  <Descriptions.Item label="描述">{faction.description || '-'}</Descriptions.Item>
                  {faction.goals && faction.goals.length > 0 && (
                    <Descriptions.Item label="目标">
                      <List
                        size="small"
                        dataSource={faction.goals}
                        renderItem={(g) => <List.Item style={{ border: 'none' }}>{g}</List.Item>}
                      />
                    </Descriptions.Item>
                  )}
                </Descriptions>
              </Card>
            </Col>
          ))}
        </Row>
      ) : (
        <Alert message="暂无派系数据" type="info" showIcon />
      )}
    </Space>
  )
}

// ============================================================================
// Phase 4: NPC 与对话产物
// ============================================================================

interface Phase4ViewerProps {
  data: Phase4Result | null | undefined
}

const Phase4Viewer: React.FC<Phase4ViewerProps> = ({ data }) => {
  // Defensive null checks
  if (!data) {
    return <Alert message="暂无数据" type="warning" showIcon />
  }

  // v5 T5 格式: player_biographies, actor_biographies
  const playerBiographies = (data as Record<string, unknown>).player_biographies as Array<{
    name: string
    basic_info?: { age?: number; gender?: string; occupation?: string }
    background?: string
    personality?: string
  }> | undefined
  const actorBiographies = (data as Record<string, unknown>).actor_biographies as Array<{
    name: string
    basic_info?: { age?: number; gender?: string; occupation?: string }
    background?: string
  }> | undefined

  // 如果是 v5 T5 格式
  if (playerBiographies || actorBiographies) {
    const players = playerBiographies || []
    const actors = actorBiographies || []
    const totalCount = players.length + actors.length

    return (
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        <Alert
          message={`共生成 ${totalCount} 个人物角色 (${players.length} 玩家 + ${actors.length} 演员)`}
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />

        {players.length > 0 && (
          <Card size="small" title={`玩家角色 (${players.length})`}>
            <Row gutter={16}>
              {players.map((player, idx) => (
                <Col span={12} key={idx}>
                  <Card size="small" title={player.name} style={{ marginBottom: 8 }}>
                    <Descriptions column={1} size="small">
                      <Descriptions.Item label="基本信息">
                        {player.basic_info?.gender} · {player.basic_info?.age}岁 · {player.basic_info?.occupation}
                      </Descriptions.Item>
                      <Descriptions.Item label="背景">
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          {player.background?.substring(0, 150)}...
                        </Text>
                      </Descriptions.Item>
                    </Descriptions>
                  </Card>
                </Col>
              ))}
            </Row>
          </Card>
        )}

        {actors.length > 0 && (
          <Card size="small" title={`演员角色 (${actors.length})`}>
            <Row gutter={16}>
              {actors.map((actor, idx) => (
                <Col span={12} key={idx}>
                  <Card size="small" title={actor.name} style={{ marginBottom: 8 }}>
                    <Descriptions column={1} size="small">
                      <Descriptions.Item label="基本信息">
                        {actor.basic_info?.gender} · {actor.basic_info?.age}岁 · {actor.basic_info?.occupation}
                      </Descriptions.Item>
                      <Descriptions.Item label="背景">
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          {actor.background?.substring(0, 150)}...
                        </Text>
                      </Descriptions.Item>
                    </Descriptions>
                  </Card>
                </Col>
              ))}
            </Row>
          </Card>
        )}
      </Space>
    )
  }

  // v4 格式
  const npcs = data.npcs || []

  return (
    <Space direction="vertical" style={{ width: '100%' }} size="middle">
      <Alert
        message={`共生成 ${npcs.length} 个 NPC`}
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
      />

      {npcs.length > 0 ? (
        <Row gutter={16}>
          {npcs.map((npc, idx) => (
            <Col span={12} key={idx}>
              <Card
                size="small"
                title={
                  <Space>
                    <TeamOutlined />
                    {npc.name || '-'}
                  </Space>
                }
                extra={<Tag color="blue">{npc.role || '-'}</Tag>}
              >
                <Descriptions column={1} size="small">
                  <Descriptions.Item label="ID">
                    <Text code>{npc.id || '-'}</Text>
                  </Descriptions.Item>
                  <Descriptions.Item label="描述">{npc.description || '-'}</Descriptions.Item>
                  <Descriptions.Item label="性格">
                    <Space wrap>
                      {(npc.personality || []).map((p, i) => (
                        <Tag key={i}>{p}</Tag>
                      ))}
                    </Space>
                  </Descriptions.Item>
                  <Descriptions.Item label="说话风格">{npc.speech_style || '-'}</Descriptions.Item>
                </Descriptions>

                {/* 对话模板 */}
                {npc.dialogue_templates && Object.keys(npc.dialogue_templates).length > 0 && (
                  <>
                    <Divider style={{ margin: '8px 0' }} />
                    <Text type="secondary">对话模板:</Text>
                    {Object.entries(npc.dialogue_templates as unknown as Record<string, string[]>).map(([type, templates]) => (
                      <Card
                        key={type}
                        size="small"
                        title={type}
                        style={{ marginTop: 8 }}
                      >
                        <List
                          size="small"
                          dataSource={templates || []}
                          renderItem={(tpl, tidx) => (
                            <List.Item style={{ border: 'none' }}>
                              <Text style={{ fontSize: 12 }}>
                                {tidx + 1}. {tpl}
                              </Text>
                            </List.Item>
                          )}
                        />
                      </Card>
                    ))}
                  </>
                )}

                {(npc.topics || []).length > 0 && (
                  <>
                    <Divider style={{ margin: '8px 0' }} />
                    <Text type="secondary">可聊话题:</Text>
                    <Space wrap style={{ marginTop: 4 }}>
                      {(npc.topics || []).map((t, i) => (
                        <Tag key={i} color="cyan">{t}</Tag>
                      ))}
                    </Space>
                  </>
                )}

                {(npc.greetings || []).length > 0 && (
                  <>
                    <Divider style={{ margin: '8px 0' }} />
                    <Text type="secondary">招呼语:</Text>
                    <List
                      size="small"
                      style={{ marginTop: 4 }}
                      dataSource={npc.greetings || []}
                      renderItem={(g) => (
                        <List.Item style={{ border: 'none' }}>
                          <Text type="secondary">"{g}"</Text>
                        </List.Item>
                      )}
                    />
                  </>
                )}
              </Card>
            </Col>
          ))}
        </Row>
      ) : (
        <Alert message="暂无 NPC 数据" type="info" showIcon />
      )}
    </Space>
  )
}

// ============================================================================
// Phase 5: 任务与时间线产物
// ============================================================================

interface Phase5ViewerProps {
  data: Phase5Result | null | undefined
}

const Phase5Viewer: React.FC<Phase5ViewerProps> = ({ data }) => {
  // Defensive null checks
  if (!data) {
    return <Alert message="暂无数据" type="warning" showIcon />
  }

  // v5 T6 格式: intersection_points
  const intersectionPoints = (data as Record<string, unknown>).intersection_points as Array<{
    id: string
    name: string
    type: string
    timing: string
    location: string
    content: string
    purpose: string
  }> | undefined

  // 如果是 v5 T6 格式
  if (intersectionPoints && intersectionPoints.length > 0) {
    return (
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        <Card size="small" title={`交集点场景 (${intersectionPoints.length})`}>
          <List
            size="small"
            dataSource={intersectionPoints}
            renderItem={(point) => (
              <List.Item>
                <Descriptions column={1} size="small" style={{ width: '100%' }}>
                  <Descriptions.Item label="场景">
                    <Tag color="blue">{point.type}</Tag>
                    {point.name}
                  </Descriptions.Item>
                  <Descriptions.Item label="时间">{point.timing}</Descriptions.Item>
                  <Descriptions.Item label="地点">{point.location}</Descriptions.Item>
                  <Descriptions.Item label="内容">
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {point.content?.substring(0, 300)}...
                    </Text>
                  </Descriptions.Item>
                  <Descriptions.Item label="目的">{point.purpose}</Descriptions.Item>
                </Descriptions>
              </List.Item>
            )}
          />
        </Card>
      </Space>
    )
  }

  // v4 格式
  const quests = data.quests || []
  const timeline = data.timeline || []

  return (
    <Space direction="vertical" style={{ width: '100%' }} size="middle">
      <Row gutter={16}>
        {/* 任务列表 */}
        <Col span={12}>
          <Card size="small" title={`任务列表 (${quests.length})`}>
            {quests.length > 0 ? (
              <List
                size="small"
                dataSource={quests}
                renderItem={(quest) => (
                  <List.Item>
                    <Descriptions column={1} size="small" style={{ width: '100%' }}>
                      <Descriptions.Item label="名称">
                        <Space>
                          <Tag>{quest.id || '-'}</Tag>
                          <Text strong>{quest.name || '-'}</Text>
                        </Space>
                      </Descriptions.Item>
                      <Descriptions.Item label="描述">{quest.description || '-'}</Descriptions.Item>
                      <Descriptions.Item label="触发条件">{quest.trigger_condition || '-'}</Descriptions.Item>
                      <Descriptions.Item label="完成条件">{quest.completion_condition || '-'}</Descriptions.Item>
                      {(quest.rewards || []).length > 0 && (
                        <Descriptions.Item label="奖励">
                          <Space wrap>
                            {(quest.rewards || []).map((r, i) => (
                              <Tag key={i} color="gold">{r}</Tag>
                            ))}
                          </Space>
                        </Descriptions.Item>
                      )}
                    </Descriptions>
                  </List.Item>
                )}
              />
            ) : (
              <Alert message="暂无任务数据" type="info" showIcon />
            )}
          </Card>
        </Col>

        {/* 时间线 */}
        <Col span={12}>
          <Card size="small" title={`时间线 (${timeline.length})`}>
            {timeline.length > 0 ? (
              <Timeline
                mode="left"
                items={timeline.map((node) => ({
                  color: 'blue',
                  label: node.trigger_time || '-',
                  children: (
                    <>
                      <Text strong>{node.name || '-'}</Text>
                      <br />
                      <Text type="secondary">{node.event || '-'}</Text>
                      {node.related_npc && (
                        <Tag color="purple" style={{ marginLeft: 8 }}>{node.related_npc}</Tag>
                      )}
                    </>
                  ),
                }))}
              />
            ) : (
              <Alert message="暂无时间线数据" type="info" showIcon />
            )}
          </Card>
        </Col>
      </Row>
    </Space>
  )
}

// ============================================================================
// Phase 6: 物品与变量产物
// ============================================================================

interface Phase6ViewerProps {
  data: Phase6Result | null | undefined
}

const Phase6Viewer: React.FC<Phase6ViewerProps> = ({ data }) => {
  // Defensive null checks
  if (!data) {
    return <Alert message="暂无数据" type="warning" showIcon />
  }

  // v5 T3 格式: relations
  const relations = (data as Record<string, unknown>).relations as Array<{
    from: string
    to: string
    type: string
    description: string
  }> | undefined

  // 如果是 v5 T3 格式
  if (relations && relations.length > 0) {
    return (
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        <Card size="small" title={`关系网络 (${relations.length})`}>
          <List
            size="small"
            dataSource={relations}
            renderItem={(rel) => (
              <List.Item>
                <Descriptions column={1} size="small" style={{ width: '100%' }}>
                  <Descriptions.Item label="关系">
                    <Space>
                      <Tag color="blue">{rel.from}</Tag>
                      <Text>→</Text>
                      <Tag color="green">{rel.to}</Tag>
                      <Tag color="purple">{rel.type}</Tag>
                    </Space>
                  </Descriptions.Item>
                  <Descriptions.Item label="描述">{rel.description}</Descriptions.Item>
                </Descriptions>
              </List.Item>
            )}
          />
        </Card>
      </Space>
    )
  }

  // v4 格式
  const items = data.items || []
  const variables = data.variables || []

  const getItemTypeColor = (type: string) => {
    switch (type) {
      case 'key_item': return 'gold'
      case 'evidence': return 'purple'
      case 'tool': return 'blue'
      case 'info': return 'default'
      default: return 'default'
    }
  }

  return (
    <Space direction="vertical" style={{ width: '100%' }} size="middle">
      <Row gutter={16}>
        {/* 物品列表 */}
        <Col span={12}>
          <Card size="small" title={`物品列表 (${items.length})`}>
            {items.length > 0 ? (
              <List
                size="small"
                dataSource={items}
                renderItem={(item) => (
                  <List.Item>
                    <Descriptions column={1} size="small" style={{ width: '100%' }}>
                      <Descriptions.Item label="名称">
                        <Space>
                          <Tag color={getItemTypeColor(item.type)}>{item.type || '-'}</Tag>
                          <Text strong>{item.name || '-'}</Text>
                        </Space>
                      </Descriptions.Item>
                      <Descriptions.Item label="描述">{item.description || '-'}</Descriptions.Item>
                      <Descriptions.Item label="获取方式">{item.acquisition || '-'}</Descriptions.Item>
                    </Descriptions>
                  </List.Item>
                )}
              />
            ) : (
              <Alert message="暂无物品数据" type="info" showIcon />
            )}
          </Card>
        </Col>

        {/* 游戏变量 */}
        <Col span={12}>
          <Card size="small" title={`游戏变量 (${variables.length})`}>
            {variables.length > 0 ? (
              <List
                size="small"
                dataSource={variables}
                renderItem={(variable) => (
                  <List.Item>
                    <Descriptions column={1} size="small" style={{ width: '100%' }}>
                      <Descriptions.Item label="名称">
                        <Space>
                          <Tag>{variable.type || '-'}</Tag>
                          <Text strong>{variable.name || '-'}</Text>
                        </Space>
                      </Descriptions.Item>
                      <Descriptions.Item label="初始值">
                        <Text code>{variable.default_value || '-'}</Text>
                      </Descriptions.Item>
                      <Descriptions.Item label="用途">{variable.usage || '-'}</Descriptions.Item>
                    </Descriptions>
                  </List.Item>
                )}
              />
            ) : (
              <Alert message="暂无变量数据" type="info" showIcon />
            )}
          </Card>
        </Col>
      </Row>
    </Space>
  )
}

// ============================================================================
// Phase 7: 资源描述产物
// ============================================================================

interface Phase7ViewerProps {
  data: Phase7Result | null | undefined
}

const Phase7Viewer: React.FC<Phase7ViewerProps> = ({ data }) => {
  // Defensive null checks
  if (!data) {
    return <Alert message="暂无数据" type="warning" showIcon />
  }

  // v5 T2 格式: player_stories, actor_stories
  const playerStories = (data as Record<string, unknown>).player_stories as Array<{
    player_id: string
    player_name: string
    story: string
  }> | undefined
  const actorStories = (data as Record<string, unknown>).actor_stories as Array<{
    actor_id: string
    actor_name: string
    story: string
  }> | undefined

  // 如果是 v5 T2 格式
  if (playerStories || actorStories) {
    const players = playerStories || []
    const actors = actorStories || []
    const totalCount = players.length + actors.length

    return (
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        <Alert
          message={`共生成 ${totalCount} 个个人故事 (${players.length} 玩家 + ${actors.length} 演员)`}
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />

        {players.length > 0 && (
          <Card size="small" title={`玩家个人故事 (${players.length})`}>
            <List
              size="small"
              dataSource={players}
              renderItem={(item) => (
                <List.Item>
                  <Descriptions column={1} size="small" style={{ width: '100%' }}>
                    <Descriptions.Item label="角色">
                      <Tag color="blue">{item.player_name}</Tag>
                    </Descriptions.Item>
                    <Descriptions.Item label="故事">
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {item.story?.substring(0, 300)}...
                      </Text>
                    </Descriptions.Item>
                  </Descriptions>
                </List.Item>
              )}
            />
          </Card>
        )}

        {actors.length > 0 && (
          <Card size="small" title={`演员个人故事 (${actors.length})`}>
            <List
              size="small"
              dataSource={actors}
              renderItem={(item) => (
                <List.Item>
                  <Descriptions column={1} size="small" style={{ width: '100%' }}>
                    <Descriptions.Item label="角色">
                      <Tag color="green">{item.actor_name}</Tag>
                    </Descriptions.Item>
                    <Descriptions.Item label="故事">
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {item.story?.substring(0, 300)}...
                      </Text>
                    </Descriptions.Item>
                  </Descriptions>
                </List.Item>
              )}
            />
          </Card>
        )}
      </Space>
    )
  }

  // v4 格式
  const resources = data.resources || {}
  const config_blocks = data.config_blocks || []
  const npc_images = resources.npc_images || []
  const scene_images = resources.scene_images || []

  return (
    <Space direction="vertical" style={{ width: '100%' }} size="middle">
      <Row gutter={16}>
        {/* NPC 头像 */}
        <Col span={12}>
          <Card size="small" title={`NPC 头像 (${npc_images.length})`}>
            {npc_images.length > 0 ? (
              <List
                size="small"
                dataSource={npc_images}
                renderItem={(img) => (
                  <List.Item>
                    <Descriptions column={1} size="small" style={{ width: '100%' }}>
                      <Descriptions.Item label="NPC ID">
                        <Tag>{img.npc_id || '-'}</Tag>
                      </Descriptions.Item>
                      <Descriptions.Item label="风格">
                        <Tag color="blue">{img.style || '-'}</Tag>
                        <Tag>{img.resolution || '-'}</Tag>
                      </Descriptions.Item>
                      <Descriptions.Item label="提示词">
                        <Paragraph
                          type="secondary"
                          ellipsis={{ rows: 2 }}
                          style={{ margin: 0, fontSize: 12 }}
                        >
                          {img.prompt || '-'}
                        </Paragraph>
                      </Descriptions.Item>
                    </Descriptions>
                  </List.Item>
                )}
              />
            ) : (
              <Alert message="暂无 NPC 头像数据" type="info" showIcon />
            )}
          </Card>
        </Col>

        {/* 场景背景 */}
        <Col span={12}>
          <Card size="small" title={`场景背景 (${scene_images.length})`}>
            {scene_images.length > 0 ? (
              <List
                size="small"
                dataSource={scene_images}
                renderItem={(scene) => (
                  <List.Item>
                    <Descriptions column={1} size="small" style={{ width: '100%' }}>
                      <Descriptions.Item label="场景 ID">
                        <Tag>{scene.scene_id || '-'}</Tag>
                      </Descriptions.Item>
                      <Descriptions.Item label="风格">{scene.style || '-'}</Descriptions.Item>
                      <Descriptions.Item label="提示词">
                        <Paragraph
                          type="secondary"
                          ellipsis={{ rows: 2 }}
                          style={{ margin: 0, fontSize: 12 }}
                        >
                          {scene.prompt || '-'}
                        </Paragraph>
                      </Descriptions.Item>
                    </Descriptions>
                  </List.Item>
                )}
              />
            ) : (
              <Alert message="暂无场景背景数据" type="info" showIcon />
            )}
          </Card>
        </Col>
      </Row>

      {/* 配置块 */}
      <Card size="small" title={`配置块 (${config_blocks.length})`}>
        {config_blocks.length > 0 ? (
          <List
            size="small"
            dataSource={config_blocks}
            renderItem={(block: { block_type?: string; block_id?: string; priority?: number; config?: Record<string, unknown> }) => (
              <List.Item>
                <Descriptions column={2} size="small" style={{ width: '100%' }}>
                  <Descriptions.Item label="类型">
                    <Tag color="blue">{block.block_type || '-'}</Tag>
                  </Descriptions.Item>
                  <Descriptions.Item label="ID">
                    <Text code>{block.block_id || '-'}</Text>
                  </Descriptions.Item>
                  <Descriptions.Item label="优先级">
                    <Tag color={(block.priority || 0) >= 80 ? 'red' : (block.priority || 0) >= 50 ? 'orange' : 'default'}>
                      {block.priority || 0}
                    </Tag>
                  </Descriptions.Item>
                  <Descriptions.Item label="配置">
                    <pre style={codeStyle}>
                      {JSON.stringify(block.config || {}, null, 2)}
                    </pre>
                  </Descriptions.Item>
                </Descriptions>
              </List.Item>
            )}
          />
        ) : (
          <Alert message="暂无配置块数据" type="info" showIcon />
        )}
      </Card>
    </Space>
  )
}

// ============================================================================
// 阶段产物查看器主组件
// ============================================================================

interface PhaseOutputViewerProps {
  phase: number
  data: unknown
}

/**
 * 阶段产物查看器
 * 根据不同阶段展示对应的结构化内容
 */
const PhaseOutputViewer: React.FC<PhaseOutputViewerProps> = ({ phase, data }) => {
  // 空数据处理
  if (!data || (Array.isArray(data) && data.length === 0)) {
    return (
      <Alert
        message="暂无产物数据"
        description="该阶段的产物数据为空，可能是因为阶段未完成执行或数据未正确保存。"
        type="warning"
        showIcon
      />
    )
  }

  // 处理对象类型
  const dataObj = data as Record<string, unknown>

  switch (phase) {
    case 1:
      return <Phase1Viewer data={dataObj as unknown as Phase1Result} />
    case 2:
      return <Phase2Viewer data={dataObj as unknown as Phase2Result} />
    case 3:
      return <Phase3Viewer data={dataObj as unknown as Phase3Result} />
    case 4:
      return <Phase4Viewer data={dataObj as unknown as Phase4Result} />
    case 5:
      return <Phase5Viewer data={dataObj as unknown as Phase5Result} />
    case 6:
      return <Phase6Viewer data={dataObj as unknown as Phase6Result} />
    case 7:
      return <Phase7Viewer data={dataObj as unknown as Phase7Result} />
    case 8:
      return (
        <Alert
          message="验证与保存阶段"
          description="此阶段负责验证所有配置块并保存到数据库，无额外产物数据。"
          type="success"
          showIcon
        />
      )
    default:
      return (
        <Alert
          message={`未知阶段 (${phase})`}
          description={
            <pre style={{ maxHeight: 200, overflow: 'auto' }}>
              {JSON.stringify(data, null, 2)}
            </pre>
          }
          type="info"
          showIcon
        />
      )
  }
}

/**
 * v5 Template 到 Phase 的映射
 */
const V5_TEMPLATE_PHASE_MAP: Record<string, number> = {
  'T1': 1, 'T2': 7, 'T3': 6, 'T4': 3, 'T5': 4, 'T6': 5, 'T7': 2,
  'T8': 8, 'T9': 8, 'T10': 8, 'T11': 8
}

/**
 * 从 AI 调用日志中获取指定阶段的响应内容
 * 支持 v4 (phase1, phase2...) 和 v5 (T1, T2...) 格式
 */
const getPhaseContentFromLogs = (
  phase: number,
  logs: Array<{
    phase?: string
    response_data?: { content?: unknown }
  }> = []
): unknown => {
  const phaseLog = logs.find((log) => {
    const phaseStr = log.phase || ''

    // v4 格式: "phase1_text_preprocess"
    const v4Match = phaseStr.match(/phase(\d+)/)
    if (v4Match && parseInt(v4Match[1], 10) === phase) {
      return !!log.response_data?.content
    }

    // v5 格式: "T1:t1_world_bible"
    const v5Match = phaseStr.match(/T(\d+)/)
    if (v5Match) {
      const templateNum = parseInt(v5Match[1], 10)
      const mappedPhase = V5_TEMPLATE_PHASE_MAP[`T${templateNum}`]
      return mappedPhase === phase && !!log.response_data?.content
    }

    return false
  })
  return phaseLog?.response_data?.content
}

/**
 * 阶段结果卡片组件
 */
interface PhaseResultCardProps {
  phaseResult: PhaseResult
  aiCallLogs?: Array<{
    phase?: string
    response_data?: { content?: unknown }
  }>
}

export const PhaseResultCard: React.FC<PhaseResultCardProps> = ({ phaseResult, aiCallLogs = [] }) => {
  // Defensive: handle undefined phaseResult
  if (!phaseResult) {
    return <Alert message="无阶段结果数据" type="warning" showIcon />
  }

  const pr: PhaseResult = phaseResult
  const phase: number = pr.phase
  // 支持 v4 (phase_name) 和 v5 (template_name)
  const phase_name: string = pr.phase_name || pr.template_name || `阶段 ${pr.phase}`
  // 支持 v4 (success: boolean) 和 v5 (status: string)
  const isSuccess: boolean = pr.success ?? pr.status === 'completed'
  const isFailed: boolean = pr.status === 'failed' || (pr.success === false && pr.status !== 'completed')
  const duration_ms: number = pr.duration_ms || 0
  const ai_calls: number = pr.ai_calls || 0
  const tokens: number = pr.tokens || 0
  const cost_yuan: number = pr.cost_yuan || 0
  // v5 可能没有 completed_at，用 updated_at 或其他字段代替
  const completed_at: string = pr.completed_at || ''
  const data = pr.data

  // 优先使用 AI 调用日志中的实际内容
  const aiGeneratedContent = getPhaseContentFromLogs(phase, aiCallLogs)

  // 获取状态标签颜色和文字
  const getStatusDisplay = () => {
    if (isSuccess) return { color: 'success' as const, text: '完成' }
    if (isFailed) return { color: 'error' as const, text: '失败' }
    return { color: 'processing' as const, text: '进行中' }
  }
  const statusDisplay = getStatusDisplay()

  return (
    <Card
      size="small"
      title={
        <Space>
          {PHASE_ICONS[phase] || <FileTextOutlined />}
          <span>Phase {phase}</span>
          <Tag color={statusDisplay.color}>
            {statusDisplay.text}
          </Tag>
          <Text type="secondary">{phase_name}</Text>
        </Space>
      }
      extra={
        <Space>
          <Text type="secondary">{duration_ms ? `${duration_ms}ms` : '-'}</Text>
          <Tag color="cyan">{tokens ? `${tokens} tokens` : '-'}</Tag>
          <Text type="success">{cost_yuan ? `¥${Number(cost_yuan).toFixed(4)}` : '-'}</Text>
        </Space>
      }
    >
      {/* 统计信息 */}
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={6}>
          <Text type="secondary">AI 调用</Text>
          <div><Text strong>{ai_calls ? `${ai_calls} 次` : '-'}</Text></div>
        </Col>
        <Col span={6}>
          <Text type="secondary">Token 消耗</Text>
          <div><Text strong>{tokens || '-'}</Text></div>
        </Col>
        <Col span={6}>
          <Text type="secondary">成本</Text>
          <div><Text strong>{cost_yuan ? `¥${Number(cost_yuan).toFixed(4)}` : '-'}</Text></div>
        </Col>
        <Col span={6}>
          <Text type="secondary">完成时间</Text>
          <div><Text strong>{completed_at ? new Date(completed_at).toLocaleTimeString('zh-CN') : '-'}</Text></div>
        </Col>
      </Row>

      {/* 产物数据 - 优先展示 AI 实际生成的内容 */}
      {(isSuccess || aiGeneratedContent) && (
        <>
          <Divider />
          <PhaseOutputViewer
            phase={phase}
            data={(aiGeneratedContent || data || {}) as Record<string, unknown>}
          />
        </>
      )}

      {/* 错误信息 */}
      {isFailed && phaseResult.error && (
        <>
          <Divider />
          <Alert message="执行失败" description={phaseResult.error} type="error" showIcon />
        </>
      )}
    </Card>
  )
}

export default PhaseOutputViewer
